// @ts-nocheck
/**
 * ツインキーパッシブマトリクス
 *
 * 属性 × カテゴリ の組み合わせでパッシブ傾向を定義
 *
 * 仕様書: twin_key_passive_system_v1.md
 * 設計書: twin_key_phase2_design.md
 */

import {
  PASSIVE_TRIGGER_TYPES,
  PASSIVE_EFFECT_TYPES,
  PASSIVE_SLOTS,
  ULTIMATE_PASSIVE_PRESETS,
} from './passiveTypes';

/**
 * 属性ごとのパッシブ傾向
 */
export const ATTRIBUTE_PASSIVE_TENDENCY = {
  '炎': {
    direction: ['attack', 'burn', 'on_kill'],
    effects: [
      PASSIVE_EFFECT_TYPES.ATK_PERCENT,
      PASSIVE_EFFECT_TYPES.DAMAGE_DEALT_PERCENT,
      PASSIVE_EFFECT_TYPES.STATUS_INFLICT_CHANCE,
    ],
    triggers: [
      PASSIVE_TRIGGER_TYPES.ON_KILL,
      PASSIVE_TRIGGER_TYPES.ON_ACTION,
    ],
    statusType: 'BURN',
  },
  '水': {
    direction: ['defense', 'hp', 'on_damage'],
    effects: [
      PASSIVE_EFFECT_TYPES.HP_PERCENT,
      PASSIVE_EFFECT_TYPES.DEF_FLAT,
      PASSIVE_EFFECT_TYPES.DAMAGE_RECEIVED_PERCENT,
    ],
    triggers: [
      PASSIVE_TRIGGER_TYPES.ON_DAMAGE_RECEIVED,
      PASSIVE_TRIGGER_TYPES.ALWAYS,
    ],
    statusType: 'FREEZE',
  },
  '光': {
    direction: ['heal', 'support', 'buff'],
    effects: [
      PASSIVE_EFFECT_TYPES.HEAL_PERCENT,
      PASSIVE_EFFECT_TYPES.BUFF_APPLY,
      PASSIVE_EFFECT_TYPES.HP_RECOVER,
    ],
    triggers: [
      PASSIVE_TRIGGER_TYPES.ALLY_SUPPORT,
      PASSIVE_TRIGGER_TYPES.TURN_START,
    ],
    statusType: 'PARALYSIS',
  },
  '闇': {
    direction: ['debuff', 'absorb', 'conditional'],
    effects: [
      PASSIVE_EFFECT_TYPES.STATUS_INFLICT_CHANCE,
      PASSIVE_EFFECT_TYPES.HP_RECOVER,
      PASSIVE_EFFECT_TYPES.DAMAGE_DEALT_PERCENT,
    ],
    triggers: [
      PASSIVE_TRIGGER_TYPES.CONDITIONAL,
      PASSIVE_TRIGGER_TYPES.ON_ACTION,
    ],
    statusType: 'CURSE',
  },
  '未来': {
    direction: ['speed', 'first_strike', 'on_action'],
    effects: [
      PASSIVE_EFFECT_TYPES.SPD_FLAT,
      PASSIVE_EFFECT_TYPES.FIRST_STRIKE,
      PASSIVE_EFFECT_TYPES.EXTRA_ACTION,
    ],
    triggers: [
      PASSIVE_TRIGGER_TYPES.ON_ACTION,
      PASSIVE_TRIGGER_TYPES.BATTLE_START,
    ],
    statusType: 'TIME_STOP',
  },
  '原始': {
    direction: ['hp', 'regen', 'poison'],
    effects: [
      PASSIVE_EFFECT_TYPES.HP_PERCENT,
      PASSIVE_EFFECT_TYPES.HP_RECOVER,
      PASSIVE_EFFECT_TYPES.STATUS_INFLICT_CHANCE,
    ],
    triggers: [
      PASSIVE_TRIGGER_TYPES.TURN_END,
      PASSIVE_TRIGGER_TYPES.ALWAYS,
    ],
    statusType: 'POISON',
  },
  'なし': {
    direction: ['balance', 'universal'],
    effects: [
      PASSIVE_EFFECT_TYPES.ATK_PERCENT,
      PASSIVE_EFFECT_TYPES.HP_PERCENT,
      PASSIVE_EFFECT_TYPES.DEF_FLAT,
    ],
    triggers: [
      PASSIVE_TRIGGER_TYPES.ALWAYS,
    ],
    statusType: 'EXHAUST',
  },
};

/**
 * カテゴリごとのパッシブ傾向
 */
export const CATEGORY_PASSIVE_TENDENCY = {
  'ドラゴン': {
    direction: ['attack', 'aoe'],
    effects: [PASSIVE_EFFECT_TYPES.ATK_PERCENT, PASSIVE_EFFECT_TYPES.DAMAGE_DEALT_PERCENT],
    roleBonus: 'attacker',
  },
  'ビースト': {
    direction: ['balance', 'speed'],
    effects: [PASSIVE_EFFECT_TYPES.SPD_FLAT, PASSIVE_EFFECT_TYPES.CRIT_RATE],
    roleBonus: 'balance',
  },
  'スピリット': {
    direction: ['heal', 'support'],
    effects: [PASSIVE_EFFECT_TYPES.HEAL_PERCENT, PASSIVE_EFFECT_TYPES.BUFF_APPLY],
    roleBonus: 'support',
  },
  'ゴーレム': {
    direction: ['defense', 'on_damage'],
    effects: [PASSIVE_EFFECT_TYPES.DEF_FLAT, PASSIVE_EFFECT_TYPES.DAMAGE_RECEIVED_PERCENT],
    roleBonus: 'tank',
  },
  'ヒューマノイド': {
    direction: ['attribute_dependent'],
    effects: [],  // 属性依存
    roleBonus: 'flexible',
  },
  'プラント': {
    direction: ['regen', 'endurance'],
    effects: [PASSIVE_EFFECT_TYPES.HP_RECOVER, PASSIVE_EFFECT_TYPES.HP_PERCENT],
    roleBonus: 'endurance',
  },
  'インセクト': {
    direction: ['speed', 'probability'],
    effects: [PASSIVE_EFFECT_TYPES.SPD_FLAT, PASSIVE_EFFECT_TYPES.EXTRA_ACTION],
    roleBonus: 'speed',
  },
  'スライム': {
    direction: ['endurance', 'split'],
    effects: [PASSIVE_EFFECT_TYPES.HP_PERCENT, PASSIVE_EFFECT_TYPES.SURVIVE],
    roleBonus: 'endurance',
  },
  '鳥': {
    direction: ['speed', 'evasion'],
    effects: [PASSIVE_EFFECT_TYPES.SPD_FLAT],
    roleBonus: 'speed',
  },
  'カラス': {
    direction: ['speed', 'evasion'],
    effects: [PASSIVE_EFFECT_TYPES.SPD_FLAT],
    roleBonus: 'speed',
  },
  '蛮族': {
    direction: ['attack', 'conditional'],
    effects: [PASSIVE_EFFECT_TYPES.ATK_PERCENT, PASSIVE_EFFECT_TYPES.DAMAGE_DEALT_PERCENT],
    roleBonus: 'attacker',
  },
  'マシン': {
    direction: ['defense', 'status_resist'],
    effects: [PASSIVE_EFFECT_TYPES.DEF_FLAT, PASSIVE_EFFECT_TYPES.STATUS_RESIST],
    roleBonus: 'tank',
  },
  '魔族': {
    direction: ['debuff', 'absorb'],
    effects: [PASSIVE_EFFECT_TYPES.STATUS_INFLICT_CHANCE, PASSIVE_EFFECT_TYPES.HP_RECOVER],
    roleBonus: 'debuffer',
  },
};

/**
 * 属性 × カテゴリ マトリクス
 * 主要な組み合わせの役割イメージ
 */
export const ATTRIBUTE_CATEGORY_MATRIX = {
  // 炎属性
  '炎_ドラゴン': { role: '超火力', ultimate: ULTIMATE_PASSIVE_PRESETS.PURSUIT },
  '炎_ビースト': { role: '攻撃型', ultimate: ULTIMATE_PASSIVE_PRESETS.CONFIRMED_CRIT },
  '炎_スピリット': { role: '攻撃バフ', ultimate: ULTIMATE_PASSIVE_PRESETS.PARTY_ATK_AURA },
  '炎_ゴーレム': { role: '反撃タンク', ultimate: ULTIMATE_PASSIVE_PRESETS.REFLECT },
  '炎_ヒューマノイド': { role: '攻撃寄り', ultimate: ULTIMATE_PASSIVE_PRESETS.EXECUTION },

  // 水属性
  '水_ドラゴン': { role: '耐久アタッカー', ultimate: ULTIMATE_PASSIVE_PRESETS.DAMAGE_LIMIT },
  '水_ビースト': { role: 'バランス', ultimate: ULTIMATE_PASSIVE_PRESETS.GUTS },
  '水_スピリット': { role: 'ヒーラー', ultimate: ULTIMATE_PASSIVE_PRESETS.HEAL_BOOST },
  '水_ゴーレム': { role: '硬タンク', ultimate: ULTIMATE_PASSIVE_PRESETS.BARRIER },
  '水_ヒューマノイド': { role: '回復寄り', ultimate: ULTIMATE_PASSIVE_PRESETS.NEAR_DEATH_HEAL },

  // 光属性
  '光_ドラゴン': { role: 'バフアタッカー', ultimate: ULTIMATE_PASSIVE_PRESETS.DOUBLE_ACTION },
  '光_ビースト': { role: 'サポート', ultimate: ULTIMATE_PASSIVE_PRESETS.INSPIRE },
  '光_スピリット': { role: 'ヒーラー', ultimate: ULTIMATE_PASSIVE_PRESETS.REVIVE_SELF },
  '光_ゴーレム': { role: '聖タンク', ultimate: ULTIMATE_PASSIVE_PRESETS.COVER },
  '光_ヒューマノイド': { role: 'バランス', ultimate: ULTIMATE_PASSIVE_PRESETS.PARTY_DEF_AURA },

  // 闇属性
  '闘_ドラゴン': { role: 'デバフアタッカー', ultimate: ULTIMATE_PASSIVE_PRESETS.LIFESTEAL },
  '闇_ビースト': { role: 'デバッファー', ultimate: ULTIMATE_PASSIVE_PRESETS.STATUS_IMMUNITY },
  '闘_スピリット': { role: '呪い系', ultimate: ULTIMATE_PASSIVE_PRESETS.INHERITANCE },
  '闇_ゴーレム': { role: '吸収タンク', ultimate: ULTIMATE_PASSIVE_PRESETS.REFLECT },
  '闇_ヒューマノイド': { role: 'デバフ寄り', ultimate: ULTIMATE_PASSIVE_PRESETS.GUARANTEED_COUNTER },

  // 未来属性
  '未来_ドラゴン': { role: '速攻アタッカー', ultimate: ULTIMATE_PASSIVE_PRESETS.FIRST_STRIKE },
  '未来_ビースト': { role: '速度型', ultimate: ULTIMATE_PASSIVE_PRESETS.DOUBLE_ACTION },
  '未来_スピリット': { role: '速度バフ', ultimate: ULTIMATE_PASSIVE_PRESETS.COPY },
  '未来_ゴーレム': { role: '先制タンク', ultimate: ULTIMATE_PASSIVE_PRESETS.INVINCIBLE_START },
  '未来_ヒューマノイド': { role: '速度寄り', ultimate: ULTIMATE_PASSIVE_PRESETS.MULTI_STRIKE },

  // 原始属性
  '原始_ドラゴン': { role: '大火力', ultimate: ULTIMATE_PASSIVE_PRESETS.PENETRATION },
  '原始_ビースト': { role: '毒アタッカー', ultimate: ULTIMATE_PASSIVE_PRESETS.EXECUTION },
  '原始_スピリット': { role: '毒/再生', ultimate: ULTIMATE_PASSIVE_PRESETS.SUPER_REGEN },
  '原始_ゴーレム': { role: '超耐久', ultimate: ULTIMATE_PASSIVE_PRESETS.DAMAGE_LIMIT },
  '原始_ヒューマノイド': { role: '耐久寄り', ultimate: ULTIMATE_PASSIVE_PRESETS.GUTS },

  // なし属性
  'なし_ドラゴン': { role: 'バランス', ultimate: ULTIMATE_PASSIVE_PRESETS.PURSUIT },
  'なし_ビースト': { role: 'バランス', ultimate: ULTIMATE_PASSIVE_PRESETS.ADAPT },
  'なし_スピリット': { role: 'バランス', ultimate: ULTIMATE_PASSIVE_PRESETS.HEAL_BOOST },
  'なし_ゴーレム': { role: 'バランス', ultimate: ULTIMATE_PASSIVE_PRESETS.FORTITUDE },
  'なし_ヒューマノイド': { role: '万能', ultimate: ULTIMATE_PASSIVE_PRESETS.WEAK_IMMUNITY },
};

/**
 * 汎用パッシブテンプレート生成
 *
 * @param {string} attribute - 属性
 * @param {string} category - カテゴリ
 * @param {string} slot - パッシブスロット
 * @returns {Object} パッシブテンプレート
 */
export const generatePassiveTemplate = (attribute, category, slot) => {
  const attrTendency = ATTRIBUTE_PASSIVE_TENDENCY[attribute] || ATTRIBUTE_PASSIVE_TENDENCY['なし'];
  const catTendency = CATEGORY_PASSIVE_TENDENCY[category] || CATEGORY_PASSIVE_TENDENCY['ヒューマノイド'];

  // 究極パッシブの場合はマトリクスから取得
  if (slot === PASSIVE_SLOTS.ULTIMATE) {
    const key = `${attribute}_${category}`;
    const matrix = ATTRIBUTE_CATEGORY_MATRIX[key];
    if (matrix?.ultimate) {
      return {
        slot,
        ...matrix.ultimate,
        source: 'matrix',
      };
    }
  }

  // 通常パッシブ: 属性とカテゴリの傾向を組み合わせ
  const effects = [...attrTendency.effects];
  if (catTendency.effects.length > 0) {
    effects.push(...catTendency.effects);
  }

  // 重複除去
  const uniqueEffects = [...new Set(effects)];

  // スロットに応じた値を設定
  const baseValues = {
    [PASSIVE_SLOTS.PASSIVE_1]: { percent: 0.10, flat: 10 },
    [PASSIVE_SLOTS.PASSIVE_2]: { percent: 0.15, flat: 15 },
    [PASSIVE_SLOTS.PASSIVE_3]: { percent: 0.20, flat: 20 },
  };

  const values = baseValues[slot] || baseValues[PASSIVE_SLOTS.PASSIVE_1];

  return {
    slot,
    trigger: attrTendency.triggers[0] || PASSIVE_TRIGGER_TYPES.ALWAYS,
    effects: uniqueEffects.slice(0, 2), // 最大2効果
    values,
    attributeTendency: attrTendency.direction,
    categoryRole: catTendency.roleBonus,
    source: 'generated',
  };
};

/**
 * マトリクスキー取得
 */
export const getMatrixKey = (attribute, category) => `${attribute}_${category}`;

/**
 * マトリクスエントリ取得
 */
export const getMatrixEntry = (attribute, category) => {
  const key = getMatrixKey(attribute, category);
  return ATTRIBUTE_CATEGORY_MATRIX[key] || null;
};
